import { LanguageEnum, t } from "@/i18n";
import {
  AuditLogSchema,
  AuditLogListResponseSchema,
  type AuditLog,
} from "@/schemas/auditLogSchema";
import {
  ClientListResponseSchema,
  ClientSchema,
  ClientsStatsSchema,
  ClientStatsSchema,
  type Client,
  type ClientListItem,
  type ClientsStats,
  type ClientStats,
  type CreateClient,
  type FlagClientPayload,
} from "@/schemas/clientSchema";
import {
  TransactionListResponseSchema,
  TransactionSchema,
  TransactionStatsSchema,
  type CreateTransactionForm,
  type Transaction,
  type TransactionItemForm,
  type TransactionListResponse,
  type TransactionStats,
  type PatchTransactionForm,
} from "@/schemas/transactionSchema";
import {
  UserListResponseSchema,
  UserSchema,
  UserStatsSchema,
  type CreateUser,
  type User,
  type UserListItem,
  type UserStats,
} from "@/schemas/userSchema";
import {
  ChargeListResponseSchema,
  ChargeSchema,
  ChargeStatsSchema,
  type Charge,
  type ChargeListItem,
  type ChargeStats,
  type CreateCharge,
} from "@/schemas/chargeSchema";
import {
  PreferenceListResponseSchema,
  PreferenceSchema,
  EnrollResponseSchema,
  SendNowResponseSchema,
  type Preference,
  type EnrollResponse,
  type SendNowResponse,
  type UpdateTelegramReport,
} from "@/schemas/preferenceSchema";
import { StatsSummarySchema, type StatsPeriod, type StatsSummary } from "@/schemas/statsSchema";
import type { ApiResponse, CursorPaginatedResponse } from "@/types/common";
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import camelcaseKeys from "camelcase-keys";
import { toast } from "sonner";

const API_URL = import.meta.env.VITE_API_URL;
const MAX_REFRESH_RETRIES = 3;

interface RetryConfig extends InternalAxiosRequestConfig {
  _retryCount?: number;
  _isRefresh?: boolean;
}

const apiClient: AxiosInstance = axios.create({
  baseURL: `${API_URL}/v1`,
  timeout: 30000,
  withCredentials: true,
});

let refreshPromise: Promise<User> | null = null;

function getSharedRefresh(): Promise<User> {
  if (!refreshPromise) {
    refreshPromise = services.refreshToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

apiClient.interceptors.response.use(
  (response) => {
    if (response.data && !(response.data instanceof Blob) && typeof response.data === "object") {
      response.data = camelcaseKeys(response.data, { deep: true });
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryConfig;
    const status = error.response?.status;
    if (status === 401 && originalRequest && !originalRequest._isRefresh) {
      originalRequest._retryCount = (originalRequest._retryCount ?? 0) + 1;
      if (originalRequest._retryCount <= MAX_REFRESH_RETRIES) {
        try {
          await getSharedRefresh();
          return apiClient(originalRequest);
        } catch {
          // refresh failed, fall through
        }
      }
    }
    if (status) {
      const errorMap: Record<number, string> = {
        400: "errors.badRequest",
        401: "errors.unauthorized",
        403: "errors.forbidden",
        404: "errors.resourceNotFound",
        429: "errors.tooManyRequests",
        500: "errors.serverError",
      };
      const isLastAttempt = (originalRequest?._retryCount ?? 0) >= MAX_REFRESH_RETRIES;
      if (status !== 401 || isLastAttempt) {
        toast.error(t(errorMap[status] || "errors.default"));
      }
    }
    return Promise.reject(error);
  },
);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getData<T>(response: any): T {
  return response.data.data;
}

const serializeApiItems = (items: TransactionItemForm[]) =>
  items.map(({ testedKarat, ...rest }) => ({
    ...rest,
    testedKarat: testedKarat ?? undefined,
    name: rest.name || "",
  }));

export const services = {
  // ── Auth ────────────────────────────────────────────────────────────────────
  getProfile: async (): Promise<User | null> => {
    const response = await apiClient.get<ApiResponse<User>>("/profile");
    const data = getData(response);
    return data ? UserSchema.parse(data) : null;
  },
  verifyCallback: async (token: string): Promise<User> => {
    const response = await apiClient.post("/auth/callback", { token });
    return response.data;
  },
  login: async (email: string, lang: LanguageEnum): Promise<void> => {
    await apiClient.post("/auth/login", { email, lang });
  },
  logout: async (): Promise<void> => {
    await apiClient.post("/auth/logout");
  },
  refreshToken: async (): Promise<User> => {
    const response = await apiClient.post<User>("/auth/refresh", null, {
      _isRefresh: true,
    } as RetryConfig);
    return response.data;
  },

  // ── User ─────────────────────────────────────────────────────────────────
  getUsers: async (params?: URLSearchParams): Promise<CursorPaginatedResponse<UserListItem>> => {
    const response = await apiClient.get<ApiResponse<CursorPaginatedResponse<UserListItem>>>(
      "/users",
      { params },
    );
    return UserListResponseSchema.parse(getData(response));
  },
  createUser: async (data: CreateUser): Promise<User> => {
    const response = await apiClient.post<ApiResponse<User>>("/users", data);
    return UserSchema.parse(getData(response));
  },
  getUser: async (id: string): Promise<User> => {
    const response = await apiClient.get<ApiResponse<User>>(`/users/${id}`);
    return UserSchema.parse(getData(response));
  },
  toggleUserActiveStatus: async (id: string): Promise<{ is_active: boolean }> => {
    const response = await apiClient.patch<ApiResponse<{ is_active: boolean }>>(
      `/users/${id}/toggle-active`,
    );
    return getData<{ is_active: boolean }>(response);
  },
  deleteUser: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/users/${id}`);
    return response.data;
  },
  getUserStats: async (userId: string): Promise<UserStats> => {
    const response = await apiClient.get<ApiResponse<UserStats>>(`/users/${userId}/stats`);
    return UserStatsSchema.parse(getData(response));
  },

  // ── Client ─────────────────────────────────────────────────────────────────
  getClients: async (
    params?: URLSearchParams,
  ): Promise<CursorPaginatedResponse<ClientListItem>> => {
    const response = await apiClient.get<ApiResponse<CursorPaginatedResponse<ClientListItem>>>(
      "/clients",
      { params },
    );
    return ClientListResponseSchema.parse(getData(response));
  },
  createClient: async (data: CreateClient): Promise<Client> => {
    const response = await apiClient.post<ApiResponse<Client>>("/clients", data);
    return ClientSchema.parse(getData(response));
  },
  getClient: async (id: string): Promise<Client> => {
    const response = await apiClient.get<ApiResponse<Client>>(`/clients/${id}`);
    return ClientSchema.parse(getData(response));
  },
  deleteClient: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/clients/${id}`);
    return response.data;
  },
  flagClient: async (
    id: string,
    data: FlagClientPayload,
  ): Promise<Client> => {
    const response = await apiClient.patch<ApiResponse<Client>>(`/clients/${id}/flag`, data);
    return ClientSchema.parse(getData(response));
  },
  getClientStats: async (clientId: string): Promise<ClientStats> => {
    const response = await apiClient.get<ApiResponse<ClientStats>>(`/clients/${clientId}/stats`);
    return ClientStatsSchema.parse(getData(response));
  },

  // ── Transaction ────────────────────────────────────────────────────────────
  getTransactions: async (params?: URLSearchParams): Promise<TransactionListResponse> => {
    const response = await apiClient.get<ApiResponse<TransactionListResponse>>("/transactions", {
      params,
    });
    return TransactionListResponseSchema.parse(getData(response));
  },
  createTransaction: async (data: CreateTransactionForm): Promise<Transaction> => {
    const response = await apiClient.post<ApiResponse<Transaction>>("/transactions", {
      ...data,
      items: serializeApiItems(data.items),
    });
    return TransactionSchema.parse(getData(response));
  },
  getTransaction: async (id: string): Promise<Transaction> => {
    const response = await apiClient.get<ApiResponse<Transaction>>(`/transactions/${id}`);
    return TransactionSchema.parse(getData(response));
  },
  deleteTransaction: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/transactions/${id}`);
    return response.data;
  },
  patchTransaction: async (id: string, data: PatchTransactionForm): Promise<void> => {
    await apiClient.patch(`/transactions/${id}`, {
      items: serializeApiItems(data.items),
    });
  },
  exportTransactionsExcel: async (params?: URLSearchParams, lang?: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(
      "/transactions/export",
      {
        params: { ...Object.fromEntries(params || []), ...(lang ? { lang } : {}) },
        responseType: "blob",
      },
    );
    return response.data;
  },

  // ── Audit Logs ─────────────────────────────────────────────────────────────
  getAuditLogs: async (params?: URLSearchParams): Promise<CursorPaginatedResponse<AuditLog>> => {
    const response = await apiClient.get<ApiResponse<CursorPaginatedResponse<AuditLog>>>(
      "/audit-logs",
      { params },
    );
    return AuditLogListResponseSchema.parse(getData(response));
  },
  getAuditLog: async (id: string): Promise<AuditLog> => {
    const response = await apiClient.get<ApiResponse<AuditLog>>(`/audit-logs/${id}`);
    return AuditLogSchema.parse(getData(response));
  },

  // ── Stats ─────────────────────────────────────────────────────────────────
  getStatsSummary: async (
    period: StatsPeriod,
    range?: { from: Date; to: Date },
  ): Promise<StatsSummary> => {
    const params = new URLSearchParams({ period });
    if (period === "custom" && range) {
      params.set("startDate", range.from.toISOString());
      params.set("endDate", range.to.toISOString());
    }
    const response = await apiClient.get<ApiResponse<StatsSummary>>("/stats/summary", { params });
    return StatsSummarySchema.parse(getData(response));
  },
  getTransactionStats: async (): Promise<TransactionStats> => {
    const response = await apiClient.get<ApiResponse<TransactionStats>>("/transactions/stats");
    return TransactionStatsSchema.parse(getData(response));
  },
  getChargeStats: async (): Promise<ChargeStats> => {
    const response = await apiClient.get<ApiResponse<ChargeStats>>("/charges/stats");
    return ChargeStatsSchema.parse(getData(response));
  },
  getClientsStats: async (): Promise<ClientsStats> => {
    const response = await apiClient.get<ApiResponse<ClientsStats>>("/clients/stats");
    return ClientsStatsSchema.parse(getData(response));
  },

  // ── Charges ───────────────────────────────────────────────────────────────
  getCharges: async (
    params?: URLSearchParams,
  ): Promise<CursorPaginatedResponse<ChargeListItem>> => {
    const response = await apiClient.get<ApiResponse<CursorPaginatedResponse<ChargeListItem>>>(
      "/charges",
      { params },
    );
    return ChargeListResponseSchema.parse(getData(response));
  },
  createCharge: async (data: CreateCharge): Promise<Charge> => {
    const response = await apiClient.post<ApiResponse<Charge>>("/charges", data);
    return ChargeSchema.parse(getData(response));
  },
  getCharge: async (id: string): Promise<Charge> => {
    const response = await apiClient.get<ApiResponse<Charge>>(`/charges/${id}`);
    return ChargeSchema.parse(getData(response));
  },
  deleteCharge: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/charges/${id}`);
    return response.data;
  },
  exportChargesExcel: async (params?: URLSearchParams, lang?: string): Promise<Blob> => {
    const response = await apiClient.get<Blob>(
      "/charges/export",
      {
        params: { ...Object.fromEntries(params || []), ...(lang ? { lang } : {}) },
        responseType: "blob",
      },
    );
    return response.data;
  },

  // ── Preferences ───────────────────────────────────────────────────────────
  getPreferences: async (params?: URLSearchParams): Promise<CursorPaginatedResponse<Preference>> => {
    const response = await apiClient.get<ApiResponse<CursorPaginatedResponse<Preference>>>(
      "/preferences",
      { params },
    );
    return PreferenceListResponseSchema.parse(getData(response));
  },
  getPreference: async (id: string): Promise<Preference> => {
    const response = await apiClient.get<ApiResponse<Preference>>(`/preferences/${id}`);
    return PreferenceSchema.parse(getData(response));
  },
  createPreference: async (data: { type: string; language: string; reportTime: string }): Promise<Preference> => {
    const response = await apiClient.post<ApiResponse<Preference>>("/preferences", {
      type: data.type,
      value: {
        language: data.language,
        reportTime: data.reportTime,
        reportedUsers: [],
      },
    });
    return PreferenceSchema.parse(getData(response));
  },
  deletePreference: async (id: string): Promise<ApiResponse> => {
    const response = await apiClient.delete<ApiResponse>(`/preferences/${id}`);
    return response.data;
  },
  enrollPreference: async (id: string): Promise<EnrollResponse> => {
    const response = await apiClient.post<ApiResponse<EnrollResponse>>(
      `/preferences/${id}/telegram-report/enroll`,
    );
    return EnrollResponseSchema.parse(getData(response));
  },
  togglePreferenceEnrollment: async (id: string): Promise<Record<string, unknown>> => {
    const response = await apiClient.put<ApiResponse<Record<string, unknown>>>(
      `/preferences/${id}/telegram-report/toggle`,
    );
    return getData<Record<string, unknown>>(response);
  },
  sendNowPreference: async (id: string): Promise<SendNowResponse> => {
    const response = await apiClient.post<ApiResponse<SendNowResponse>>(
      `/preferences/${id}/telegram-report/send-now`,
    );
    return SendNowResponseSchema.parse(getData(response));
  },
  sendToMePreference: async (id: string): Promise<SendNowResponse> => {
    const response = await apiClient.post<ApiResponse<SendNowResponse>>(
      `/preferences/${id}/telegram-report/send-to-me`,
    );
    return SendNowResponseSchema.parse(getData(response));
  },
  updateTelegramReport: async (id: string, data: UpdateTelegramReport): Promise<Preference> => {
    const response = await apiClient.put<ApiResponse<Preference>>(
      `/preferences/${id}/telegram-report`,
      data,
    );
    return PreferenceSchema.parse(getData(response));
  },
};
